<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";
import Icon from "@components/icons/Icon.vue";
import {
  encriptar,
  encriptarCampo,
} from "@composables/use_encript.js";
import ShoppingCartModal from "@components/shopping_cart/ShoppingCartModal.vue";
import { useShoppingCartStore } from "@composables/use_shopping_cart_store";
import { useAuthStore } from "@composables/use_auth_store";

const { token, getTokenStatus } = useAuthStore();

const props = defineProps({
  items: Array,
  showCart: Boolean,
  classes: String,
  up: Boolean
});

const { showShoppingCart, toggleShoppingCartModal, processCart } =
  useShoppingCartStore();

const menuRef = ref();
const showMenu = ref(false);

var soloPirotecnia = ref(false);

onMounted(() => {
  document.addEventListener("click", onClickOutside, true);
  const dominioURL = window.location.href;
  soloPirotecnia.value = dominioURL.includes("?pirotecnia");
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onClickOutside, true);
});

function onClickOutside(event) {
  if (
    showMenu.value &&
    menuRef.value &&
    !menuRef.value.contains(event.target)
  ) {
    showMenu.value = false;
  }
}

function clearLocalStorage() {
  localStorage.setItem(encriptarCampo("token"), encriptar(""));
  localStorage.setItem(encriptarCampo("cliente"), encriptar(""));
  localStorage.setItem(encriptarCampo("email"), encriptar(""));
  localStorage.setItem(encriptarCampo("saleId"), encriptar(""));
  localStorage.setItem(encriptarCampo("preferenceId"), encriptar(""));
  localStorage.setItem(encriptarCampo("cartModified"), encriptar("false"));
}

function logout() {
  clearLocalStorage();
  getTokenStatus();
  alert("Sesión cerrada correctamente");
  window.location.href = window.location.origin;
}

function toggleMenu() {
  showMenu.value = !showMenu.value;
}
</script>

<template>
  <div class="relative flex items-center" ref="menuRef">
    
    <!-- Hamburger Trigger -->
    <Icon
      :name="'menu'"
      :width="35"
      :classes="'menu-btn cursor-pointer transition-transform duration-200 hover:scale-110'"
      @ontap="toggleMenu"
    />

    <!-- Dropdown Menu (WhatsApp Style) -->
    <transition
      enter-active-class="transition ease-out duration-200 origin-top-left"
      enter-from-class="opacity-0 scale-95 translate-y-1"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition ease-in duration-150 origin-top-left"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-1"
    >
      <div
        v-if="showMenu"
        class="absolute top-[calc(100%+10px)] left-0 z-[9999] bg-white rounded-2xl shadow-[0_20px_60px_-12px_rgba(0,0,0,0.25)] border border-slate-100 py-2 min-w-[210px]"
      >
        <!-- Header -->
        <div class="flex items-center gap-2 px-4 pb-2 pt-1 border-b border-slate-100 mb-1">
          <Icon :name="'menu'" :width="16" class="opacity-50" />
          <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400">Menú</span>
        </div>

        <!-- Navigation Links -->
        <div class="flex flex-col gap-0.5 px-2">
          
          <!-- Dynamic items -->
          <a
            v-for="(item, index) in props.items"
            :key="`menu_link_${index}`"
            :href="item.link"
            class="group flex items-center px-3 py-2.5 rounded-xl text-[13px] font-medium text-slate-700 hover:bg-[#e41b1b] hover:text-white transition-all duration-150"
            @click="showMenu = false"
          >
            <span>{{ item.name }}</span>
          </a>

          <!-- Auth Links -->
          <template v-if="!token">
            <a
              :href="`${soloPirotecnia ? '/login/?pirotecnia' : '/login'}`"
              class="group flex items-center px-3 py-2.5 rounded-xl text-[13px] font-medium text-slate-700 hover:bg-[#e41b1b] hover:text-white transition-all duration-150"
              @click="showMenu = false"
            >
              <span>Iniciar Sesión</span>
            </a>
            <a
              :href="`${soloPirotecnia ? '/register/?pirotecnia' : '/register'}`"
              class="group flex items-center px-3 py-2.5 rounded-xl text-[13px] font-medium text-slate-700 hover:bg-[#e41b1b] hover:text-white transition-all duration-150"
              @click="showMenu = false"
            >
              <span>Registrarse</span>
            </a>
          </template>

          <!-- User Logged In Links -->
          <template v-if="token">
            <a
              :href="`${soloPirotecnia ? '/mis_compras/?pirotecnia' : '/mis_compras'}`"
              class="group flex items-center px-3 py-2.5 rounded-xl text-[13px] font-medium text-slate-700 hover:bg-[#e41b1b] hover:text-white transition-all duration-150"
              @click="showMenu = false"
            >
              <span>Mis compras</span>
            </a>
            <a
              :href="`${soloPirotecnia ? '/change_password/?pirotecnia' : '/change_password'}`"
              class="group flex items-center px-3 py-2.5 rounded-xl text-[13px] font-medium text-slate-700 hover:bg-[#e41b1b] hover:text-white transition-all duration-150"
              @click="showMenu = false"
            >
              <span>Cambiar contraseña</span>
            </a>
            <button
              class="group flex items-center px-3 py-2.5 rounded-xl text-[13px] font-medium text-[#e41b1b] hover:bg-[#e41b1b] hover:text-white transition-all duration-150 text-left"
              @click="logout"
            >
              <span>Cerrar Sesión</span>
            </button>
          </template>
        </div>
      </div>
    </transition>

  </div>

  <ShoppingCartModal
    v-if="showShoppingCart"
    @oncloseshoppingcart="toggleShoppingCartModal"
    @onProcessCart="processCart"
  />
</template>

<style scoped>
.menu-btn :deep(img) {
  /* Normalize to black first, then apply red filter */
  filter: brightness(0) saturate(100%) invert(18%) sepia(95%) saturate(7483%) hue-rotate(359deg) brightness(91%) contrast(92%);
}

/* Ensure Icons within links turn white on hover */
.group:hover :deep(img) {
  filter: brightness(0) invert(1) !important;
}
</style>
